<template>
	<div class="mt-4">
		<h2 class="text-left">{{ $t(headingKey) }}</h2>
		<div class="d-flex justify-content-start">
			<div
				v-if="applicantProfile && jobOffer"
				class="mb-4 mobile-overflow-table"
			>
				<table class="table mb-0 table-striped">
					<thead v-if="match" class="thead-dark">
						<tr>
							<th></th>
							<th class="w-34">
								{{ $t(`general.label`) }}
							</th>
							<th class="w-33">
								{{ $t('general.job-offer') }}
							</th>
							<th class="w-33">
								{{ $t('general.applicant-profile') }}
							</th>
						</tr>
					</thead>
					<tbody>
						<tr>
							<th
								:class="
									pensumExists.both
										? $getMatchClasses(match, 'pensum')
										: 'match-unknown'
								"
								width="50"
							>
								<Icon
									class="w-5"
									:icon="
										getMatchIcon(
											pensumExists.both
												? $getMatchClasses(
														match,
														'pensum'
												  )
												: 'unknown'
										)
									"
								/>
							</th>
							<th>{{ $t('profile.pensum') }}</th>
							<td v-if="pensumExists.jobOffer">
								<div
									v-if="
										jobOffer.min_pensum ===
											jobOffer.max_pensum
									"
								>
									{{ jobOffer.max_pensum }}%
								</div>
								<div v-else>
									{{
										jobOffer.pensum
											? jobOffer.pensum
											: jobOffer.min_pensum +
											  ' ' +
											  (jobOffer.max_pensum !=
											  jobOffer.min_pensum
													? `- ${jobOffer.max_pensum}`
													: '')
									}}
									%
								</div>
							</td>
							<td v-else>{{ $t('abbreviation.no-answer') }}</td>
							<td v-if="pensumExists.applicantProfile">
								<div
									v-if="
										applicantProfile.min_pensum ===
											applicantProfile.max_pensum
									"
								>
									{{ applicantProfile.pensum }}%
								</div>
								<div v-else>
									{{
										applicantProfile.pensum
											? applicantProfile.pensum
											: applicantProfile.min_pensum +
											  ' ' +
											  (applicantProfile.max_pensum !=
											  applicantProfile.min_pensum
													? `- ${applicantProfile.max_pensum}`
													: '')
									}}
									%
								</div>
							</td>
							<td v-else>{{ $t('abbreviation.no-answer') }}</td>
						</tr>
						<tr v-if="jobOffer.salary">
							<th
								:class="$getMatchClasses(match, 'salary')"
								width="50"
							>
								<Icon
									class="w-5"
									:icon="
										getMatchIcon(
											$getMatchClasses(match, 'salary')
										)
									"
								/>
							</th>
							<th>{{ $t('profile.salary') }}</th>
							<td>
								{{ jobOffer.salary_currency || 'CHF' }}
								{{
									$formatCurrency(jobOffer.salary).replace(
										'.00',
										''
									)
								}}
								{{ $t('abbreviation.per-annum') }}
							</td>
						</tr>
						<tr v-else-if="match && match.percents.salary !== null">
							<th
								:class="$getMatchClasses(match, 'salary')"
								width="50"
							>
								<Icon
									class="w-5"
									:icon="
										getMatchIcon(
											$getMatchClasses(match, 'salary')
										)
									"
								/>
							</th>
							<th>{{ $t('profile.salary') }}</th>
							<td>
								{{
									match.percents.salary > 0
										? $t('salary.in-range')
										: $t('salary.not-in-range')
								}}
							</td>
							<td>
								{{
									match.percents.salary > 0
										? $t('salary.in-range')
										: $t('salary.not-in-range')
								}}
							</td>
						</tr>
						<tr v-else-if="match && match.percents.salary === null">
							<th class="match-unknown" width="50">
								<Icon class="w-5" :icon="['fas', 'question']" />
							</th>
							<th>{{ $t('profile.salary') }}</th>
							<td>{{ $t('abbreviation.no-answer') }}</td>
							<td>{{ $t('abbreviation.no-answer') }}</td>
						</tr>
						<tr v-if="jobOffer.employmentType">
							<th
								v-if="!applicantProfile.employmentType"
								class="match-unknown"
								width="50"
							>
								<Icon class="w-5" :icon="['fas', 'question']" />
							</th>
							<th
								v-else
								:class="
									$getMatchClasses(match, 'employmentType')
								"
								width="50"
							>
								<Icon
									class="w-5"
									:icon="
										getMatchIcon(
											$getMatchClasses(
												match,
												'employmentType'
											)
										)
									"
								/>
							</th>
							<th>{{ $t('profile.employmentType') }}</th>
							<td>
								{{
									jobOffer.employmentType[
										$getLocaleDbColumn('name')
									]
								}}
							</td>
							<td v-if="applicantProfile.employmentType">
								{{
									applicantProfile.employmentType[
										$getLocaleDbColumn('name')
									]
								}}
							</td>
							<td v-else>{{ $t('abbreviation.no-answer') }}</td>
						</tr>
						<tr>
							<th :class="calculatedDateMatch" width="50">
								<Icon
									class="w-5"
									:icon="getMatchIcon(calculatedDateMatch)"
								/>
							</th>
							<th>{{ $t('profile.start_date') }}</th>
							<td
								v-if="
									jobOffer.start_date &&
										dateIsPast(jobOffer.start_date)
								"
							>
								{{ $t('form.per-immediately') }}
							</td>
							<td v-else-if="jobOffer.start_date">
								{{ $t('general.available-from') }}
								{{ renderDate(jobOffer.start_date) }}
							</td>
							<td v-else>{{ $t('start-date.by-agreement') }}</td>

							<td
								v-if="
									applicantProfile.start_date &&
										dateIsPast(applicantProfile.start_date)
								"
							>
								{{ $t('form.per-immediately') }}
							</td>
							<td v-else-if="applicantProfile.start_date">
								{{ $t('general.available-from') }}
								{{ renderDate(applicantProfile.start_date) }}
							</td>
							<td v-else>{{ $t('start-date.flexible') }}</td>
						</tr>
					</tbody>
				</table>
			</div>
			<div v-else-if="jobOffer">
				<div
					v-if="jobOffer.pensum"
					class="px-4 py-2 mb-2 mr-2 border rounded-pill"
					:class="$getMatchClasses(match, 'pensum')"
				>
					<Icon :icon="['far', 'business-time']" />
					{{ jobOffer.pensum }}%
				</div>
				<div
					v-else-if="jobOffer.min_pensum || jobOffer.max_pensum"
					class="px-4 py-2 mb-2 mr-2 border rounded-pill"
					:class="$getMatchClasses(match, 'pensum')"
				>
					<div v-if="jobOffer.min_pensum === jobOffer.max_pensum">
						<Icon :icon="['far', 'business-time']" />
						{{ jobOffer.max_pensum }}%
					</div>
					<div v-else>
						<Icon :icon="['far', 'business-time']" />
						{{ jobOffer.min_pensum }}
						{{
							jobOffer.max_pensum
								? `- ${jobOffer.max_pensum}`
								: ''
						}}%
					</div>
				</div>
				<div
					v-if="jobOffer.salary"
					class="px-4 py-2 mb-2 mr-2 border rounded-pill"
					:class="$getMatchClasses(match, 'salary')"
				>
					<Icon :icon="['far', 'money-bill']" />
					{{ jobOffer.salary_currency || 'CHF' }}
					{{ $formatCurrency(jobOffer.salary).replace('.00', '') }}
					{{ $t('abbreviation.per-annum') }}
				</div>
				<div
					v-else-if="match && match.percents.salary !== null"
					class="px-4 py-2 mb-2 mr-2 border rounded-pill"
					:class="$getMatchClasses(match, 'salary')"
				>
					<Icon :icon="['far', 'money-bill']" />
					{{
						match.percents.salary > 0
							? $t('salary.in-range')
							: $t('salary.not-in-range')
					}}
				</div>
				<div
					v-else-if="match && match.percents.salary === null"
					class="px-4 py-2 mb-2 mr-2 border rounded-pill"
					:class="$getMatchClasses(match, 'salary')"
				>
					<Icon :icon="['far', 'money-bill']" />
					{{ $t('abbreviation.no-answer') }}
				</div>
				<div
					v-if="jobOffer.employmentType"
					class="px-4 py-2 mb-2 mr-2 border rounded-pill"
					:class="$getMatchClasses(match, 'employmentType')"
				>
					<Icon :icon="['far', 'hourglass-half']" />
					{{ jobOffer.employmentType[$getLocaleDbColumn('name')] }}
				</div>
				<div
					v-if="jobOffer.start_date"
					class="px-4 py-2 mb-2 mr-2 border rounded-pill"
					:class="$getMatchClasses(match, 'start_date')"
				>
					<Icon :icon="['far', 'stopwatch']" />
					{{ $t('general.available-from') }}
					{{ renderDate(jobOffer.start_date) }}
				</div>
			</div>
			<div v-else-if="applicantProfile">
				<div
					v-if="applicantProfile.pensum"
					class="px-4 py-2 mb-2 mr-2 border rounded-pill"
					:class="$getMatchClasses(match, 'pensum')"
				>
					<Icon :icon="['far', 'business-time']" />
					{{ applicantProfile.pensum }}%
				</div>
				<div
					v-else-if="
						applicantProfile.min_pensum ||
							applicantProfile.max_pensum
					"
					class="px-4 py-2 mb-2 mr-2 border rounded-pill"
					:class="$getMatchClasses(match, 'pensum')"
				>
					<Icon :icon="['far', 'business-time']" />
					{{ applicantProfile.min_pensum }}
					{{
						applicantProfile.max_pensum
							? `- ${applicantProfile.max_pensum}`
							: ''
					}}%
				</div>
				<div
					v-if="applicantProfile.salary"
					class="px-4 py-2 mb-2 mr-2 border rounded-pill"
					:class="$getMatchClasses(match, 'salary')"
				>
					<Icon :icon="['far', 'money-bill']" />
					{{ applicantProfile.salary_currency || 'CHF' }}
					{{
						$formatCurrency(applicantProfile.salary).replace(
							'.00',
							''
						)
					}}
					{{ $t('abbreviation.per-annum') }}
				</div>
				<div
					v-else-if="match && match.percents.salary !== null"
					class="px-4 py-2 mb-2 mr-2 border rounded-pill"
					:class="$getMatchClasses(match, 'salary')"
				>
					<Icon :icon="['far', 'money-bill']" />
					{{
						match.percents.salary > 0
							? $t('salary.in-range')
							: $t('salary.not-in-range')
					}}
				</div>
				<div
					v-else-if="match && match.percents.salary === null"
					class="px-4 py-2 mb-2 mr-2 border rounded-pill"
					:class="$getMatchClasses(match, 'salary')"
				>
					<Icon :icon="['far', 'money-bill']" />
					{{ $t('abbreviation.no-answer') }}
				</div>
				<div
					v-if="applicantProfile.employmentType"
					class="px-4 py-2 mb-2 mr-2 border rounded-pill"
					:class="$getMatchClasses(match, 'employmentType')"
				>
					<Icon :icon="['far', 'hourglass-half']" />
					{{
						applicantProfile.employmentType[
							$getLocaleDbColumn('name')
						]
					}}
				</div>
				<div
					v-if="applicantProfile.start_date"
					class="px-4 py-2 mb-2 mr-2 border rounded-pill"
					:class="$getMatchClasses(match, 'start_date')"
				>
					<Icon :icon="['far', 'stopwatch']" />
					{{ $t('general.available-from') }}
					{{ renderDate(applicantProfile.start_date) }}
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import moment from 'moment'
import Icon from '~/components/ui/common/Icon'

export default {
	components: {
		Icon
	},
	props: {
		jobOffer: {
			type: Object,
			default: null
		},
		match: {
			type: Object,
			default: null
		},
		applicantProfile: {
			type: Object,
			default: null
		},
		headingKey: {
			type: String,
			default: 'general.information'
		}
	},
	computed: {
		pensumExists() {
			const jobOfferPensum = this.jobOffer
				? !!this.jobOffer.pensum ||
				  !!(this.jobOffer.min_pensum && this.jobOffer.max_pensum)
				: false
			const applicantProfilePensum = this.applicantProfile
				? !!this.applicantProfile.pensum ||
				  !!(
						this.applicantProfile.min_pensum &&
						this.applicantProfile.max_pensum
				  )
				: false
			return {
				jobOffer: jobOfferPensum,
				applicantProfile: applicantProfilePensum,
				both: jobOfferPensum && applicantProfilePensum
			}
		},
		calculatedDateMatch() {
			if (
				!this.jobOffer?.start_date ||
				!this.applicantProfile?.start_date
			)
				return 'matched'

			const date1 = new Date(this.jobOffer.start_date)
			const date2 = new Date(this.applicantProfile.start_date)
			if (!date1 || !date2) return 'match-unknown'

			const daysBetween = this.getDaysBetweenDates(date1, date2)
			if (daysBetween <= 36) return 'matched'

			const today = new Date()
			const date1IsPast = date1 < today
			const date2IsPast = date2 < today

			if (date1IsPast && date2IsPast) return 'matched'

			if (date1IsPast !== date2IsPast) {
				const upcomingDate = date1IsPast ? date2 : date1
				if (this.getDaysBetweenDates(upcomingDate, today) <= 36) {
					return 'matched'
				}
				return 'not-matched'
			}

			return 'not-matched'
		}
	},
	methods: {
		renderDate(date, options = null) {
			return moment(date, options).format('DD.MM.YYYY')
		},
		getDaysBetweenDates(date1, date2) {
			const difference = Math.abs(date1 - date2)
			return Math.ceil(difference / (1000 * 3600 * 24))
		},
		dateIsPast(date) {
			return new Date(date) <= new Date()
		},
		getMatchIcon(matchClass) {
			if (matchClass === 'matched') return ['far', 'check']
			if (matchClass === 'not-matched') return ['far', 'times']
			return ['fas', 'question']
		}
	}
}
</script>
